import { ChangeDetectionStrategy, Component } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppStateService } from 'src/app/core/services/app-state.service';

export interface PatientAppSetupView {
  isLoading: boolean;
  isLoaded: boolean;
  hasLoadFailed: boolean;
  isActive: boolean;
}

const UNAVAILABLE_STATUS_VIEW: PatientAppSetupView = {
  isLoading: false,
  isLoaded: false,
  hasLoadFailed: true,
  isActive: false
};

/**
 * Read-only Clinic Setup section for the Patient App (PAMP): shows whether the clinic is
 * subscribed and what the app offers. Nothing is saved or configured from here.
 */
@Component({
  selector: 'csi-patient-app-setup',
  templateUrl: './patient-app-setup.component.html',
  styleUrls: ['./patient-app-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientAppSetupComponent {
  public readonly benefitColumns = [
    {
      iconName: 'local_hospital',
      titleKey: 'clinicSetupPatientApp.benefits.clinicTitle',
      benefitKeys: [
        'clinicSetupPatientApp.benefits.clinic.enroll',
        'clinicSetupPatientApp.benefits.clinic.plans',
        'clinicSetupPatientApp.benefits.clinic.tests',
        'clinicSetupPatientApp.benefits.clinic.orders',
        'clinicSetupPatientApp.benefits.clinic.compliance',
        'clinicSetupPatientApp.benefits.clinic.reach'
      ]
    },
    {
      iconName: 'person',
      titleKey: 'clinicSetupPatientApp.benefits.patientsTitle',
      benefitKeys: [
        'clinicSetupPatientApp.benefits.patients.plan',
        'clinicSetupPatientApp.benefits.patients.tests',
        'clinicSetupPatientApp.benefits.patients.education',
        'clinicSetupPatientApp.benefits.patients.products',
        'clinicSetupPatientApp.benefits.patients.clinicInfo',
        'clinicSetupPatientApp.benefits.patients.reach'
      ]
    }
  ];

  public readonly view$: Observable<PatientAppSetupView> = combineLatest([
    this.appStateService.isPatientAppActive$,
    this.appStateService.pampClinicConfigLoadStatus$
  ]).pipe(
    map(([isActive, loadStatus]) => ({
      isLoading: loadStatus === 'loading',
      isLoaded: loadStatus === 'loaded',
      hasLoadFailed: loadStatus === 'failed',
      isActive
    })),
    catchError(() => of(UNAVAILABLE_STATUS_VIEW))
  );

  constructor(private appStateService: AppStateService) {}

  public trackByTitleKey(index: number, benefitColumn: { titleKey: string }): string {
    return benefitColumn.titleKey;
  }

  public trackByBenefitKey(index: number, benefitKey: string): string {
    return benefitKey;
  }
}
